import React from "react";
import { formatDimensions } from "./mitelData";

/**
 * MitelBanknoteCard — scheda singola banconota.
 *
 * Regole visive (uniformazione ufficiale):
 *  - TUTTE le 11 banconote condividono lo STESSO ingombro visivo della
 *    banconota 5 MITEL. Nessuna progressione dimensionale nella UI.
 *  - Il contenitore usa l'aspect-ratio FISICO della 5 MITEL (120/62).
 *    Le immagini con aspect-ratio leggermente diverso vengono adattate
 *    con `object-fit: contain` (nessun crop, nessuna deformazione).
 *  - Le dimensioni fisiche ufficiali della singola banconota restano
 *    riportate come informazione testuale, non come dimensione grafica.
 *  - Nessuna estetica finanziaria: nessun €, $, grafici, ticker.
 */

// Riferimento visivo ufficiale: la 5 MITEL. Tutte le altre banconote
// vengono renderizzate con lo stesso ingombro.
const REFERENCE_ASPECT = "120 / 62";
const REFERENCE_MOBILE_PCT = 88;
const REFERENCE_DESKTOP_MAX_REM = 22;

export default function MitelBanknoteCard({ banknote, testId }) {
  const b = banknote;

  return (
    <article
      data-testid={testId}
      className="w-full flex flex-col items-center text-center"
    >
      {/* Meta top: valore + equivalenza + principio narrativo */}
      <div className="mb-4">
        <div
          data-testid={`${testId}-label`}
          className="text-xl sm:text-2xl font-light text-white tracking-tight leading-none"
        >
          {b.label}
        </div>
        <div className="mt-1.5 text-[11px] tracking-[0.24em] uppercase text-emerald-300/85">
          {b.equivalent} · {formatDimensions(b)}
        </div>
        {b.principle && (
          <div className="mt-3 text-[12px] sm:text-[13px] tracking-[0.28em] uppercase text-white/85">
            {b.principle}
          </div>
        )}
      </div>

      {/* Immagine banconota — ingombro uniforme al riferimento 5 MITEL,
          nessun crop, nessuna deformazione (object-fit: contain). */}
      <div className="w-full flex justify-center">
        <div
          className="relative w-full rounded-xl overflow-hidden shadow-[0_20px_60px_-24px_rgba(16,185,129,0.35)] ring-1 ring-white/10 bg-black/20"
          style={{
            aspectRatio: REFERENCE_ASPECT,
            width: `${REFERENCE_MOBILE_PCT}%`,
            maxWidth: `${REFERENCE_DESKTOP_MAX_REM}rem`,
          }}
        >
          <img
            src={b.image}
            alt={b.alt}
            loading="lazy"
            decoding="async"
            data-testid={`${testId}-image`}
            className="absolute inset-0 w-full h-full object-contain select-none"
            draggable={false}
          />
        </div>
      </div>

      {/* Personaggio + opera */}
      <div className="mt-5">
        <div className="text-[13px] sm:text-sm text-white/85 font-light">
          <span className="text-white">{b.character.name}</span>
          <span className="mx-2 text-white/30">—</span>
          <span className="tracking-[0.24em] uppercase text-emerald-300/90 text-[11px] sm:text-[12px]">
            {b.character.focus}
          </span>
        </div>
        <div className="mt-1 text-[12px] sm:text-[13px] text-white/60 font-light">
          <span>{b.opera.name}</span>
          <span className="mx-2 text-white/25">—</span>
          <span>{b.opera.location}</span>
        </div>
      </div>

      {/* Descrizione personaggio + opera */}
      <div className="mt-4 max-w-xl mx-auto space-y-3">
        <p className="text-[12px] sm:text-[13px] text-white/70 font-light leading-relaxed">
          {b.character.bio}
        </p>
        <p className="text-[12px] sm:text-[13px] text-white/70 font-light leading-relaxed">
          {b.opera.bio}
        </p>
      </div>

      {/* Relazione simbolica */}
      <div className="mt-4 w-full max-w-xl mx-auto rounded-xl border border-emerald-400/20 bg-emerald-400/[0.05] px-4 py-3">
        <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-300/80 mb-1">
          Relazione simbolica
        </div>
        <p className="text-[12px] sm:text-[13px] text-emerald-50/90 font-light leading-relaxed">
          {b.symbolic}
        </p>
      </div>

      {/* Significato del taglio temporale */}
      <div className="mt-4 max-w-xl mx-auto">
        <p className="text-[12px] sm:text-[13px] text-white/75 font-light leading-relaxed">
          {b.meaning}
        </p>
      </div>

      {/* Frase finale della banconota */}
      <div className="mt-5 max-w-xl mx-auto">
        <p className="text-[13px] sm:text-sm text-white font-light leading-snug tracking-tight italic">
          {b.closing}
        </p>
      </div>
    </article>
  );
}
